import React, { Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { LayoutDashboard, Store, Settings as SettingsIcon, Users, FileText, Compass } from 'lucide-react';
import { useEffect, useState } from 'react';
import { supabase } from './lib/supabase';
import { isCurrentUserAdmin } from './lib/admin';
import { enforceEmailConfirmation } from './lib/auth';
import Navbar from './components/Navbar';
import Sidebar from './components/Sidebar';
import AdminImpersonationBanner from './components/AdminImpersonationBanner';
import { UserProfileProvider } from './context/UserProfileContext';
import Toast from './components/Toast';
import { isDiscoveryWorkshopHost } from './lib/discovery-workshop';
import { canEditDiscovery } from './lib/discovery-live';

const discoveryFeatureEnabled = isDiscoveryWorkshopHost(
  window.location.hostname,
  import.meta.env.VITE_DISCOVERY_ENABLED === 'true'
);

// Lazy load page components
const Landing = React.lazy(() => import('./pages/Landing'));
const Dashboard = React.lazy(() => import('./pages/Dashboard'));
const MyStore = React.lazy(() => import('./pages/MyStore'));
const Settings = React.lazy(() => import('./pages/Settings'));
const StripeOnboarding = React.lazy(() => import('./pages/StripeOnboarding'));
const PaymentSuccess = React.lazy(() => import('./pages/PaymentSuccess'));
const Login = React.lazy(() => import('./pages/Login'));
const ForgotPassword = React.lazy(() => import('./pages/ForgotPassword'));
const ResetPassword = React.lazy(() => import('./pages/ResetPassword'));
const Signup = React.lazy(() => import('./pages/Signup'));
const CheckEmail = React.lazy(() => import('./pages/CheckEmail'));
const ConfirmEmail = React.lazy(() => import('./pages/ConfirmEmail'));
const PublicStore = React.lazy(() => import('./pages/PublicStore'));
const AdminAccounts = React.lazy(() => import('./pages/AdminAccounts'));
const AdminAuditLogs = React.lazy(() => import('./pages/AdminAuditLogs'));
const LegalPage = React.lazy(() => import('./pages/LegalPage'));
const Resources = React.lazy(() => import('./pages/Resources'));
const ResourceArticle = React.lazy(() => import('./pages/ResourceArticle'));
const Discover = React.lazy(() => import('./pages/Discover'));
const DiscoveryResults = React.lazy(() => import('./pages/DiscoveryResults'));
const AdminDiscovery = React.lazy(() => import('./pages/AdminDiscovery'));

const navigation = [
  { name: 'Dashboard', href: '/dashboard', icon: LayoutDashboard },
  { name: 'My Store', href: '/store', icon: Store },
  { name: 'Settings', href: '/settings', icon: SettingsIcon },
];

// Admin navigation
const adminNavigation = [
  { name: 'Dashboard', href: '/dashboard', icon: LayoutDashboard },
  { name: 'My Store', href: '/store', icon: Store },
  { name: 'Admin Accounts', href: '/admin/accounts', icon: Users },
  { name: 'Audit Logs', href: '/admin/audit-logs', icon: FileText },
  ...(discoveryFeatureEnabled ? [{ name: 'Discovery Editor', href: '/admin/discovery', icon: Compass }] : []),
  { name: 'Settings', href: '/settings', icon: SettingsIcon },
];

// Loading component
function LoadingSpinner() {
  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center">
      <div className="animate-spin rounded-full h-8 w-8 border-2 border-blue-500 border-t-transparent"></div>
    </div>
  );
}

// Protected route wrapper component
function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean | null>(null);
  const [isAdmin, setIsAdmin] = useState<boolean>(false);
  const [isDiscoveryEditor, setIsDiscoveryEditor] = useState<boolean>(false);
  const [redirectEmail, setRedirectEmail] = useState<string | null>(null);

  useEffect(() => {
    let disposed = false;
    let refreshInterval: number | null = null;
    // Check current auth state and set up session refresh
    const checkAuthAndSetupRefresh = async () => {
      const { data: { session } } = await supabase.auth.getSession();

      if (session?.user) {
        const gate = await enforceEmailConfirmation(session.user);
        if (gate.blocked) {
          setRedirectEmail(gate.email);
          setIsAuthenticated(false);
          setIsAdmin(false);
          setIsDiscoveryEditor(false);
          return;
        }
      }

      setIsAuthenticated(!!session);

      // Check if user is admin
      if (session) {
        try {
          const adminStatus = await isCurrentUserAdmin();
          setIsAdmin(adminStatus);
        } catch (error) {
          console.error('Error checking admin status:', error);
          setIsAdmin(false);
        }
        if (discoveryFeatureEnabled) {
          try {
            const editorAccess = await canEditDiscovery();
            setIsDiscoveryEditor(editorAccess.allowed);
          } catch (error) {
            console.error('Error checking Discovery editor status:', error);
            setIsDiscoveryEditor(false);
          }
        } else {
          setIsDiscoveryEditor(false);
        }
      }

      // Refresh session every 10 minutes while this protected route is mounted.
      if (disposed) return;
      refreshInterval = window.setInterval(async () => {
        const { error } = await supabase.auth.refreshSession();
        if (error) {
          console.error('Error refreshing session:', error);
          setIsAuthenticated(false);
        }
      }, 10 * 60 * 1000); // 10 minutes
    };

    checkAuthAndSetupRefresh();

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      if (session?.user) {
        const gate = await enforceEmailConfirmation(session.user);
        if (gate.blocked) {
          setRedirectEmail(gate.email);
          setIsAuthenticated(false);
          setIsAdmin(false);
          return;
        }
      }

      setIsAuthenticated(!!session);
      if (!session) {
        setIsAdmin(false);
        setIsDiscoveryEditor(false);
      }
    });

    return () => {
      disposed = true;
      if (refreshInterval !== null) window.clearInterval(refreshInterval);
      subscription.unsubscribe();
    };
  }, []);

  // Show loading state while checking auth
  if (isAuthenticated === null) {
    return <LoadingSpinner />;
  }

  if (!isAuthenticated) {
    if (redirectEmail) {
      return <Navigate to="/check-email" replace state={{ email: redirectEmail }} />;
    }
    return <Navigate to="/login" replace />;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <AdminImpersonationBanner />
      <Navbar />
      <div className="flex">
        <Sidebar navigation={isAdmin ? adminNavigation : isDiscoveryEditor && discoveryFeatureEnabled
          ? [...navigation, { name: 'Discovery Editor', href: '/admin/discovery', icon: Compass }]
          : navigation} />
        <main className="flex-1 p-8">
          {children}
        </main>
      </div>
    </div>
  );
}

function App() {
  const discoveryEnabled = discoveryFeatureEnabled;
  return (
    <UserProfileProvider>
      <Toast />
      <Router>
        <Suspense fallback={<LoadingSpinner />}>
          <Routes>
            {/* Public routes */}
            <Route path="/" element={<Landing />} />
            <Route path="/login" element={<Login />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/signup" element={<Signup />} />
            <Route path="/check-email" element={<CheckEmail />} />
            <Route path="/confirm-email" element={<ConfirmEmail />} />
            <Route path="/success" element={<PaymentSuccess />} />
            <Route path="/payment-success" element={<PaymentSuccess />} />
            <Route path="/privacy" element={<LegalPage kind="privacy" />} />
            <Route path="/terms" element={<LegalPage kind="terms" />} />
            <Route path="/resources" element={<Resources />} />
            <Route path="/resources/:slug" element={<ResourceArticle />} />
            {discoveryEnabled && <Route path="/discover" element={<Discover />} />}
            {discoveryEnabled && <Route path="/discover/search" element={<DiscoveryResults />} />}
            {discoveryEnabled && <Route path="/discover/workshop" element={<Navigate to="/admin/discovery" replace />} />}
            
            {/* Admin routes */}
            {discoveryEnabled && <Route path="/admin/discovery" element={<ProtectedRoute><AdminDiscovery /></ProtectedRoute>} />}
            {discoveryEnabled && <Route path="/admin/discovery/preview" element={<ProtectedRoute><Discover stage="draft" /></ProtectedRoute>} />}
            {discoveryEnabled && <Route path="/admin/discovery/preview/search" element={<ProtectedRoute><DiscoveryResults stage="draft" /></ProtectedRoute>} />}
            <Route
              path="/admin/accounts"
              element={
                <ProtectedRoute>
                  <AdminAccounts />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/audit-logs"
              element={
                <ProtectedRoute>
                  <AdminAuditLogs />
                </ProtectedRoute>
              }
            />
            
            {/* Protected routes */}
            <Route
              path="/dashboard"
              element={
                <ProtectedRoute>
                  <Dashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/store/*"
              element={
                <ProtectedRoute>
                  <MyStore />
                </ProtectedRoute>
              }
            />
            <Route
              path="/settings"
              element={
                <ProtectedRoute>
                  <Settings />
                </ProtectedRoute>
              }
            />
            <Route
              path="/stripe/onboarding"
              element={
                <ProtectedRoute>
                  <StripeOnboarding />
                </ProtectedRoute>
              }
            />

            {/* Public store route - must be last to avoid conflicts */}
            <Route path="/:username" element={<PublicStore />} />
            
            {/* Catch-all redirect to home */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Suspense>
      </Router>
    </UserProfileProvider>
  );
}

export default App;
